import { Download, FileSpreadsheet } from "lucide-react";
import React, { useState } from "react";
import { toast } from "react-toastify";
import CustomModal from "../../../../components/CustomModal";
import api from "../../../../services/api";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  currentFilters: any;
}

const ExportModal: React.FC<Props> = ({ isOpen, onClose, currentFilters }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    if ((startDate && !endDate) || (!startDate && endDate)) {
      toast.warning("Selecione o período completo.");
      return;
    }

    try {
      setLoading(true);

      const body: any = {
        ...currentFilters,
        order_by: "razao_social",
        order_direction: "asc",
      };

      if (startDate && endDate) {
        body.data_inicial = startDate;
        body.data_final = endDate;
      }

      const response = await api.post("/listar/transportadoras/exportar", body, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      let nomeArquivo = "transportadoras.xlsx";
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches?.[1]) {
          nomeArquivo = matches[1].replaceAll("'", "").replaceAll('"', "");
        }
      }

      link.setAttribute("download", nomeArquivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);

      toast.success("Exportação realizada com sucesso!");
      onClose();
    } catch (error) {
      console.error("Erro na exportação:", error);
      toast.error("Erro ao exportar arquivo.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <CustomModal title="Exportar Transportadoras" onClose={onClose}>
      <div className="p-8 flex flex-col gap-4 w-full">
        <div className="-mt-6 mb-2">
          <p className="text-[#666] text-sm font-normal">
            Os filtros aplicados na listagem são mantidos na exportação.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold text-[#666] uppercase tracking-wider">
            Período de modificação (opcional)
          </label>
          <p className="text-xs text-[#888] font-normal leading-snug">
            Exporta apenas transportadoras cadastradas ou editadas no intervalo
            (data da última alteração). Em branco, inclui todas.
          </p>
          <div className="flex gap-4 mt-1">
            <div className="flex flex-col w-full">
              <span className="text-xs text-[#666] font-medium mb-1.5 ml-1">Início</span>
              <div className="bg-[#F5F6FA] rounded-xl px-4 py-3 border border-transparent focus-within:border-[#062D4E] transition-colors">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full bg-transparent text-sm text-[#1E2121] focus:outline-none font-medium"
                />
              </div>
            </div>
            <div className="flex flex-col w-full">
              <span className="text-xs text-[#666] font-medium mb-1.5 ml-1">Fim</span>
              <div className="bg-[#F5F6FA] rounded-xl px-4 py-3 border border-transparent focus-within:border-[#062D4E] transition-colors">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full bg-transparent text-sm text-[#1E2121] focus:outline-none font-medium"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 mt-2">
          <label className="text-xs font-bold text-[#666] uppercase tracking-wider">
            Formato do Arquivo
          </label>
          <div className="flex items-center gap-3 p-4 rounded-xl border-2 border-[#E5E7EB] bg-white ring-1 ring-[#062D4E]">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#ECFDF5] text-[#10B981]">
              <FileSpreadsheet size={20} className="stroke-[2.5]" />
            </div>
            <span className="font-semibold text-[#1E2121]">Excel</span>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-[#1E2121] border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-semibold"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            onClick={handleExport}
            className="px-8 py-2.5 bg-[#062D4E] text-white rounded-lg hover:bg-[#0a3d6a] transition-colors text-sm font-semibold flex items-center gap-2 shadow-lg shadow-[#062D4E]/20"
            disabled={loading}
          >
            <Download size={18} />
            {loading ? "Exportando..." : "Exportar"}
          </button>
        </div>
      </div>
    </CustomModal>
  );
};

export default ExportModal;
